/**
 * Betriebs-Kopf für alle Gastseiten (Design-Durchsicht Entscheidung 2, Antoine 26.09.2026):
 * Angebotsseite, Kundenportal, Rechnungsadresse und öffentlicher Chat zeigen oben dieselbe Leiste
 * mit Kachel + Namen des Betriebs, damit ein Gast sieht, von wem die Seite kommt.
 *
 * Name: wer einen besseren Namen aus der Public-API hat (Angebotsseite: `contact.company_name`),
 * reicht ihn herein; sonst Name aus `GET /api/public/betrieb` (sql/535: tenants.name + Logo, ohne
 * Login); sonst der Mandant aus der Subdomain, lesbar gemacht („storia“ → „Storia“).
 * Logo: ist `logo_url` gesetzt, ein `<img>` statt der Buchstaben-Kachel.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Logo } from "./components";

/** Mandanten-Slug aus dem Host; auf localhost (keine Subdomain) der Dev-Mandant. */
export function mandantAusHost(hostname = window.location.hostname, devTenant = import.meta.env.VITE_DEV_TENANT as string | undefined): string {
  const teile = hostname.split(".");
  if (teile.length < 2 || hostname === "127.0.0.1") return devTenant || "";
  return teile[0] ?? "";
}

/** „storia“ → „Storia“, „tenant-a“ → „Tenant A“. */
export function lesbarerMandant(slug: string): string {
  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function betriebsName(ausApi?: string | null): string {
  return ausApi?.trim() || lesbarerMandant(mandantAusHost());
}

export type Marke = { name?: string | null; logo_url?: string | null; privacy_url?: string | null; impressum_url?: string | null; opentable_url?: string | null };

/** Name, Logo, Datenschutz- und Impressum-Link aus `GET /api/public/betrieb`; `null`, solange nichts da ist (Rückfall: Slug + Kachel). */
export function useBetriebsMarke(): Marke | null {
  const [marke, setMarke] = useState<Marke | null>(null);
  useEffect(() => {
    let aktiv = true;
    fetch("/api/public/betrieb")
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { data?: Marke | null } | null) => {
        if (aktiv) setMarke(j?.data ?? null);
      })
      .catch(() => {}); // Rückfall: lesbarer Slug + Buchstaben-Kachel
    return () => {
      aktiv = false;
    };
  }, []);
  return marke;
}

export function BetriebsKopf({ name }: { name?: string | null }) {
  const marke = useBetriebsMarke();
  const anzeige = betriebsName(name?.trim() || marke?.name);
  if (!anzeige) return null;
  return (
    <div className="flex items-center gap-3 border-b border-outline-variant/40 bg-surface-container-lowest px-margin-mobile py-3.5 dark:border-outline/30 dark:bg-inverse-surface md:px-margin-desktop">
      {marke?.logo_url ? (
        <img src={marke.logo_url} alt={anzeige} className="h-[30px] max-w-[120px] shrink-0 rounded-[9px] object-contain" />
      ) : (
        <Logo size={30} radiusClassName="rounded-[9px]" textClassName="text-[16px]" buchstabe={anzeige.charAt(0).toUpperCase()} />
      )}
      <div data-testid="betriebs-kopf-name" className="truncate text-[14px] font-bold tracking-[0.04em] text-on-surface dark:text-inverse-on-surface">
        {anzeige}
      </div>
    </div>
  );
}

/** Kopf + weiße Karte (Radius 16) auf Creme — der Rahmen der schlichten Gast-Formularseiten. */
export function GastRahmen({ name, children, breit = false }: { name?: string | null; children: ReactNode; breit?: boolean }) {
  return (
    <div className="min-h-screen bg-background">
      <BetriebsKopf name={name} />
      <div className={`mx-auto px-4 py-8 md:py-12 ${breit ? "max-w-3xl" : "max-w-lg"}`}>
        <div className="rounded-2xl border border-[#EFE8DA] bg-surface-container-lowest p-6 shadow-[0_8px_18px_-10px_rgba(45,38,26,.14)] dark:border-white/10 dark:bg-inverse-surface md:p-8">
          {children}
        </div>
      </div>
    </div>
  );
}
